import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useSiteMode } from '../context/SiteModeContext';
import { languageNames } from '../data/translations';
import Logo from './Logo';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';

const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const { features } = useSiteMode();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) {
      document.body.style.overflow = '';
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileMenuOpen]);

  const navLinks = [
    features.services && { href: '#what-we-do', label: t.nav.whatWeDo },
    features.experience && { href: '#experience', label: t.nav.experience },
    features.portfolio && { href: '#portfolio', label: t.nav.portfolio },
    features.philosophy && { href: '#philosophy', label: t.nav.philosophy },
    features.discovery && { href: '#contact', label: t.nav.contact },
  ].filter(Boolean);

  const scrollToSection = (href) => {
    setMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-[120] transition-all duration-500 ${
        isScrolled || mobileMenuOpen || langMenuOpen
          ? 'bg-black/75 backdrop-blur-xl border-b border-zinc-900/80'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="/" className="text-white hover:opacity-80 transition-opacity">
            <Logo size="small" variant="simple" />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className="relative text-sm text-zinc-500 hover:text-white transition-colors duration-300 tracking-wide group"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-cyan-400 group-hover:w-full transition-all duration-300" />
              </button>
            ))}

            {/* Language Selector */}
            <div className="relative ml-4">
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-2 text-sm text-zinc-500 hover:text-white transition-colors duration-300 px-3 py-2 border border-zinc-800 hover:border-zinc-700"
              >
                <Globe className="w-4 h-4" />
                <span className="uppercase text-xs tracking-wider">{language}</span>
                <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${langMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {langMenuOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-40"
                    onClick={() => setLangMenuOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-48 max-h-80 overflow-y-auto bg-zinc-950 border border-zinc-800 shadow-2xl z-50">
                    {Object.entries(languageNames).map(([code, name]) => (
                      <button
                        key={code}
                        onClick={() => {
                          setLanguage(code);
                          setLangMenuOpen(false);
                        }}
                        className={`w-full px-4 py-3 text-left text-sm transition-colors hover:bg-zinc-900 ${
                          language === code ? 'text-cyan-400 bg-cyan-500/5' : 'text-zinc-400'
                        }`}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-zinc-400 hover:text-white transition-colors"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        className={`lg:hidden fixed inset-0 z-[110] transition-all duration-500 ${
          mobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-zinc-950/92 supports-[backdrop-filter]:bg-zinc-950/75" />
        <div
          className="absolute inset-0"
          style={{
            backdropFilter: 'blur(22px)',
            WebkitBackdropFilter: 'blur(22px)',
          }}
        />

        <nav className="relative h-full overflow-y-auto max-w-7xl mx-auto px-6 pt-28 pb-12 flex flex-col">
          {navLinks.map((link, index) => (
            <button
              key={link.href}
              onClick={() => scrollToSection(link.href)}
              className="text-left text-2xl font-light text-zinc-300 hover:text-white transition-colors py-4 border-b border-zinc-900"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {link.label}
            </button>
          ))}
          
          {/* Mobile Language Selector */}
          <div className="mt-12">
            <div className="flex items-center gap-2 text-zinc-500 mb-4">
              <Globe className="w-4 h-4" />
              <span className="text-xs tracking-widest uppercase">Language</span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(languageNames).map(([code, name]) => (
                <button
                  key={code}
                  onClick={() => {
                    setLanguage(code);
                    setMobileMenuOpen(false);
                  }}
                  className={`px-3 py-3 text-xs tracking-wider border transition-all ${
                    language === code 
                      ? 'bg-cyan-500/10 border-cyan-500/50 text-cyan-400' 
                      : 'border-zinc-800 text-zinc-500 hover:border-zinc-700 hover:text-zinc-300'
                  }`}
                >
                  {code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
